import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useCart } from "../context/CartContext";

const links = [
  { to: "/shop", label: "Shop" },
  { to: "/build", label: "Build a Stack" },
  { to: "/shop?goal=recovery", label: "Recover" },
  { to: "/shop?goal=lean", label: "Get Lean" },
  { to: "/rewards", label: "Rewards" },
];

export const Navbar = () => {
  const { count, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMobile(false), [loc]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "backdrop-blur-xl bg-ink-900/70 border-b border-white/5" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="w-full bg-ink-800/95 backdrop-blur border-b border-white/5 py-1.5" data-testid="compliance-ribbon">
        <p className="text-center font-mono-tech text-[9px] md:text-[10px] uppercase tracking-[0.22em] text-zinc-400 px-4">
          <span className="text-accent">⚠</span> For laboratory research use only · Not for human
          consumption · Strictly 18+
        </p>
      </div>
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo-link">
          <span className="h-2.5 w-2.5 bg-accent block group-hover:rotate-45 transition-transform duration-300" />
          <span className="font-display font-extrabold tracking-tight text-lg md:text-xl leading-none">
            RECOVERY&nbsp;SUPPS
            <span className="block font-mono-tech text-[9px] tracking-[0.35em] text-zinc-500 font-normal mt-0.5">
              AUSTRALIA
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <Link
              key={l.label}
              to={l.to}
              className="font-mono-tech text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-white transition-colors"
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setOpen(true)}
            className="relative flex items-center gap-2 px-3.5 py-2 border border-[#27272A] hover:border-accent transition-colors group"
            data-testid="cart-button"
          >
            <ShoppingBag size={17} className="text-white" />
            <span className="hidden sm:block font-mono-tech text-xs uppercase tracking-wider text-zinc-300 group-hover:text-white">
              Cart
            </span>
            {count > 0 && (
              <span
                className="absolute -top-2 -right-2 bg-accent text-white text-[10px] font-bold h-5 min-w-5 px-1 flex items-center justify-center rounded-full"
                data-testid="cart-count"
              >
                {count}
              </span>
            )}
          </button>
          <button
            className="lg:hidden text-white"
            onClick={() => setMobile((m) => !m)}
            data-testid="mobile-menu-toggle"
          >
            {mobile ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {mobile && (
        <div className="lg:hidden border-t border-white/5 bg-ink-900/95 backdrop-blur-xl px-5 py-5 flex flex-col gap-4">
          {links.map((l) => (
            <Link
              key={l.label}
              to={l.to}
              className="font-mono-tech text-sm uppercase tracking-[0.2em] text-zinc-300"
            >
              {l.label}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
};
